import { useRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Avatar,
  Badge,
  Box,
  Button,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Popover,
  Tooltip,
  Typography,
  Divider,
} from '@mui/material';
import BellIcon from '../../icons/Bell';
import ChatAltIcon from '../../icons/ChatAlt';
import CheckIcon from '@mui/icons-material/Check';
import EastIcon from '@mui/icons-material/East';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import dayjs from 'dayjs';
import Scrollbar from '../Scrollbar';

const iconsMap = {
  success: CheckIcon,
  info: ChatAltIcon,
  action: EastIcon,
  warning: CloseIcon,
  warningLight: WarningAmberIcon,
};

function NotificationsPopover({
  handleClick,
  handleClickMultipleMarkRead,
  notifications,
  fetchMoreNotifications,
  fetchGoBackNotifications,
  setOpen,
  open,
}) {
  const anchorRef = useRef(null);

  // console.log(user);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <>
      <Tooltip title="Notificaciones">
        <IconButton color="inherit" ref={anchorRef} onClick={handleOpen} size="large">
          <Badge color="error" badgeContent={notifications.filter((obj) => obj.notificationRead === false).length}>
            <BellIcon fontSize="small" />
          </Badge>
        </IconButton>
      </Tooltip>
      <Popover
        anchorEl={anchorRef.current}
        anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
        onClose={handleClose}
        open={open}
        PaperProps={{
          sx: { width: 520 }, // change era 320, tambien puede ser '100%'
        }}
      >
        <Box sx={{ p: 2 }}>
          <Typography color="textPrimary" variant="h6">
            Notificaciones
          </Typography>
        </Box>
        <Divider />
        {notifications.length === 0 ? (
          <Box sx={{ p: 2 }}>
            <Typography color="textPrimary" variant="subtitle2">
              No hay notificaciones
            </Typography>
          </Box>
        ) : (
          <>
            {/* En móvil el Scrollbar es un Box sin alto: el scroll lo hace este Box, o la lista pasa
                por debajo de los botones. El alto baja en pantallas bajas para que quepa el popover. */}
            <Box sx={{ height: 'min(400px, calc(100vh - 200px))', overflowY: 'auto' }}>
              <Scrollbar>
                <List disablePadding>
                  {notifications.map((notification) => {
                    const Icon = iconsMap[notification.notificationType];
                    // Las leídas van en gris y con el ícono atenuado para distinguirlas de las nuevas.
                    const leida = Boolean(notification.notificationRead);
                    const variant = leida ? 'body2' : 'subtitle2';

                    return (
                      <ListItem
                        divider
                        key={notification.id}
                        sx={{ py: 0 }} // change no estaba este css
                      >
                        <ListItemAvatar sx={{ opacity: leida ? 0.5 : 1 }}>
                          {notification.notificationType === 'success' && (
                            <Avatar sx={{ backgroundColor: 'success.main', color: 'success.contrastText' }}>
                              <Icon fontSize="small" />
                            </Avatar>
                          )}
                          {notification.notificationType === 'warningLight' && (
                            <Avatar sx={{ backgroundColor: 'warningLight.main', color: 'warningLight.contrastText' }}>
                              <Icon fontSize="small" />
                            </Avatar>
                          )}
                          {notification.notificationType === 'warning' && (
                            <Avatar sx={{ backgroundColor: 'error.main', color: 'error.contrastText' }}>
                              <Icon fontSize="small" />
                            </Avatar>
                          )}
                          {notification.notificationType === 'info' && (
                            <Avatar sx={{ backgroundColor: 'info.main', color: 'info.contrastText' }}>
                              <Icon fontSize="small" />
                            </Avatar>
                          )}
                          {notification.notificationType === 'action' && (
                            <Avatar sx={{ backgroundColor: 'primary.main', color: 'primary.contrastText' }}>
                              <Icon fontSize="small" />
                            </Avatar>
                          )}
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Box>
                              <Typography color={leida ? 'textSecondary' : 'textPrimary'} variant={variant}>
                                {notification.notificationTitle}
                              </Typography>
                              <Link
                                sx={{
                                  cursor: 'pointer',
                                  color: leida ? 'text.secondary' : 'text.primary',
                                  textDecoration: 'underline',
                                  textDecorationColor: '#cccccc80',
                                }}
                                variant={variant}
                                onClick={() => handleClick(notification.id)}
                                component={RouterLink}
                                to={notification.notificationLink}
                                state={notification?.notificationState || null}
                              >
                                {notification.notificationDescription}
                              </Link>
                            </Box>
                          }
                          secondary={
                            <Typography color="textSecondary" variant="caption">
                              {dayjs(
                                new Date(
                                  notification.createdAt.seconds * 1000 + notification.createdAt.nanoseconds / 1000000
                                )
                              ).format('DD MMM YYYY, h:mm a')}
                            </Typography>
                          }
                        />
                      </ListItem>
                    );
                  })}
                </List>
              </Scrollbar>
            </Box>
            <Divider />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', px: 1, py: 0.5 }}>
              <Box>
                <Button color="primary2" size="small" variant="text" onClick={() => fetchGoBackNotifications()}>
                  Volver
                </Button>
                <Button color="primary2" size="small" variant="text" onClick={() => fetchMoreNotifications()}>
                  Anteriores
                </Button>
              </Box>
              <Button color="primary2" size="small" variant="text" onClick={() => handleClickMultipleMarkRead()}>
                Marcar todas leídas
              </Button>
            </Box>
          </>
        )}
      </Popover>
    </>
  );
}

export default NotificationsPopover;
